import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Notification } from '@teentack/shared';
import { BellIcon } from '@/components/ui/Icons';
import { Skeleton } from '@/components/ui/Skeleton';
import { relativeTime } from '@/lib/relative-time';
import {
  useMarkNotificationsRead,
  useNotifications,
  useUnreadCount,
} from './use-notifications';

/**
 * The bell. [Teen step 19, Parent step 19]
 *
 * Opens to the eight most recent notifications rather than the whole inbox —
 * enough to answer "did anything happen?" without becoming a second page. The
 * count is polled, because the stack has no realtime transport yet.
 */

function Row({ item, onNavigate }: { item: Notification; onNavigate: () => void }) {
  const body = (
    <>
      <div className="flex items-start gap-2">
        {!item.isRead && (
          <span
            aria-hidden="true"
            className="mt-1.5 size-2 shrink-0 rounded-full bg-brand-600"
          />
        )}
        <div className={item.isRead ? 'min-w-0 pl-4' : 'min-w-0'}>
          <p className="text-sm font-semibold text-ink">{item.title}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{item.message}</p>
          <p className="mt-1 text-[0.7rem] font-medium text-slate-400">
            {relativeTime(item.createdAt)}
          </p>
        </div>
      </div>
    </>
  );

  if (!item.link) {
    return <li className="px-4 py-3">{body}</li>;
  }

  return (
    <li>
      <Link
        to={item.link}
        onClick={onNavigate}
        className="block px-4 py-3 transition hover:bg-brand-50/60"
      >
        {body}
      </Link>
    </li>
  );
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const unread = useUnreadCount();
  // Only fetched once the panel is open — the badge alone does not need bodies.
  const recent = useNotifications({ pageSize: 8 }, open);
  const markRead = useMarkNotificationsRead();

  const count = unread.data?.unread ?? 0;

  // Clicking away or pressing Escape closes the panel.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent): void {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        className="relative rounded-xl p-2 text-slate-600 transition duration-200 hover:bg-slate-100 hover:text-ink"
      >
        <BellIcon className="size-5" />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex min-w-4.5 items-center justify-center rounded-full bg-red-500 px-1 py-0.5 text-[0.6rem] font-bold text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-88 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-line bg-white shadow-lg">
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <h2 className="text-sm font-bold text-ink">Notifications</h2>
            {count > 0 && (
              <button
                type="button"
                onClick={() => markRead.mutate(undefined)}
                disabled={markRead.isPending}
                className="text-xs font-semibold text-brand-600 transition hover:underline disabled:opacity-50"
              >
                {markRead.isPending ? 'Marking…' : 'Mark all read'}
              </button>
            )}
          </div>

          {recent.isPending && (
            <div className="space-y-3 p-4" aria-busy="true">
              <Skeleton className="h-10" radius="md" />
              <Skeleton className="h-10" radius="md" />
            </div>
          )}

          {recent.data?.items.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-slate-500">
              Nothing yet. We will tell you when something needs you.
            </p>
          )}

          {recent.data && recent.data.items.length > 0 && (
            <ul className="max-h-96 divide-y divide-line overflow-y-auto">
              {recent.data.items.map((item) => (
                <Row key={item.id} item={item} onNavigate={() => setOpen(false)} />
              ))}
            </ul>
          )}

          <div className="border-t border-line bg-canvas px-4 py-2.5 text-center">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="text-xs font-semibold text-brand-600 transition hover:underline"
            >
              See all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
